import { createSlice } from "@reduxjs/toolkit";
import { AibleClientFactory } from "client/aible/factory";
import { ProjectUtils } from "types/project";
import { CommentableType, RecordType } from "types/base";
function getTypeFromCommentable(commentable) {
    if (ProjectUtils.isProjectAssumption(commentable)) {
        return CommentableType.PROJECT_ASSUMPTION;
    }
    else if (ProjectUtils.isProjectScenario(commentable)) {
        return CommentableType.PROJECT_SCENARIO;
    }
    else if (ProjectUtils.isProjectTask(commentable)) {
        return CommentableType.PROJECT_TASK;
    }
    throw new Error(`Unsupported commentable: ${commentable}`);
}
const COMMENT_CLIENT = AibleClientFactory.getInstance().getCommentClient();
const initialState = {
    [CommentableType.COMMENT]: {},
    [CommentableType.PROJECT_ASSUMPTION]: {},
    [CommentableType.PROJECT_SCENARIO]: {},
    [CommentableType.PROJECT_SEGMENT_ASSUMPTION]: {},
    [CommentableType.PROJECT_TASK]: {},
    [CommentableType.TRAINING]: {},
    [CommentableType.REPORT]: {},
    [CommentableType.REPORT_CHART]: {}
};
function updateStateWithComment(state, comment) {
    return {
        ...state,
        [comment.entity_type]: {
            ...(state[comment.entity_type] ?? {}),
            [comment.entity_id]: {
                ...(state[comment.entity_type]?.[comment.entity_id] ?? {}),
                [comment.id]: {
                    ...(state[comment.entity_type]?.[comment.entity_id]?.[comment.id] ?? {}),
                    ...comment
                }
            }
        }
    };
}
const commentSlice = createSlice({
    name: "comment",
    initialState: initialState,
    reducers: {
        updateComment(state, { payload }) {
            return updateStateWithComment(state, payload);
        },
        updateComments(state, { payload }) {
            return payload.reduce(updateStateWithComment, state);
        }
    }
});
export const { updateComment, updateComments } = commentSlice.actions;
export default commentSlice.reducer;
export function getComments(state, commentable) {
    const type = getTypeFromCommentable(commentable);
    return Object.values(state.comment[type][commentable.id] ?? {})
        .filter((comment) => comment.deleted == RecordType.NotDeleted)
        .sort((a, b) => b.id - a.id);
}
export function fetchComments(commentable) {
    return (dispatch) => {
        const type = getTypeFromCommentable(commentable);
        return COMMENT_CLIENT.getComments(type, commentable.id).then((comments) => {
            dispatch(commentSlice.actions.updateComments(comments));
            return comments;
        });
    };
}
export function fetchComment(commentId) {
    return (dispatch) => {
        return COMMENT_CLIENT.getComment(commentId).then((comment) => {
            dispatch(updateComment(comment));
            return comment;
        });
    };
}
export function fetchMissingComments(commentable) {
    return (dispatch, getState) => {
        const currentComments = getComments(getState(), commentable);
        if (currentComments.length == commentable.comment_count) {
            return Promise.resolve(currentComments);
        }
        return dispatch(fetchComments(commentable));
    };
}
export function updateCommentAndSave(commentId, update) {
    return (dispatch) => {
        return COMMENT_CLIENT.updateComment(commentId, update).then((comment) => {
            dispatch(updateComment(comment));
            return comment;
        });
    };
}
export function likeComment(commentId) {
    return rateComment(commentId, "LIKE");
}
export function dislikeComment(commentId) {
    return rateComment(commentId, "DISLIKE");
}
export function rateComment(commentId, feedback) {
    return (dispatch) => {
        return COMMENT_CLIENT.addCommentRating(commentId, { feedback }).then(() => dispatch(fetchComment(commentId)));
    };
}
export function addSubComment(commentId, message) {
    return (dispatch) => {
        return COMMENT_CLIENT.addSubComment(commentId, { message }).then((comment) => {
            dispatch(updateComment(comment));
            return dispatch(fetchComment(commentId));
        });
    };
}
export function deleteComment(commentId) {
    return (dispatch) => {
        return COMMENT_CLIENT.deleteComment(commentId).then((comment) => {
            dispatch(updateComment(comment));
            return comment;
        });
    };
}
